import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import {
	Box,
	Heading,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	HStack,
	Badge,
	Button,
	Flex,
	Text,
	Tooltip,
	InputGroup,
	InputLeftElement,
	Input,
	Select,
	Center,
	Spinner,
	SimpleGrid,
} from '@chakra-ui/react';
import AdminNavbar from '../../component/adminNavbar';
import Alert from '../../component/alert';
import { ViewIcon, SearchIcon } from '@chakra-ui/icons';
import {
	useReactTable,
	createColumnHelper,
	flexRender,
	getCoreRowModel,
} from '@tanstack/react-table';
import { getAllAdminOrders } from '../../redux/actions/orderActions'; // Assuming you have this action
import { getAllFacilityList } from '../../redux/actions/facilityActions'; // Import the new action
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';

const PAGE_SIZE = 25;

const Order = ({
	getAllAdminOrders,
	orders,
	adminorderloading,
	getAllFacilityList,
	facilitiesList,
	facilitiesLoading,
}) => {
	const [offset, setOffset] = useState(0);
	const [orderIdFilter, setOrderIdFilter] = useState('');
	const [facilityFilter, setFacilityFilter] = useState('');
	const [emailFilter, setEmailFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [isSearching, setIsSearching] = useState(false);

	const orderCount = Number(orders?.count) || 0;

	useEffect(() => {
		getAllFacilityList();
	}, [getAllFacilityList]);

	useEffect(() => {
		getAllAdminOrders(
			offset,
			orderIdFilter,
			facilityFilter,
			emailFilter,
			statusFilter,
			startDate,
			endDate
		).then(() => {
			if (isSearching) {
				setIsSearching(false); // Reset search trigger
			}
		});
	}, [offset, isSearching]);

	const handleSearchClick = () => {
		setOrderIdFilter((v) => v.trim());
		setEmailFilter((v) => v.trim());
		setOffset(0); // Reset to the first page for a new search
		setIsSearching(true); // Trigger the useEffect to fetch data
	};

	const handleClearClick = () => {
		setOrderIdFilter('');
		setFacilityFilter('');
		setEmailFilter('');
		setStatusFilter('');
		setStartDate('');
		setEndDate('');
		setOffset(0);
		setIsSearching(true);
	};

	const navigate = useNavigate();
	const viewOrder = (row) => {
		navigate(`/admin/orders/${row.original.order_id}`);
	};

	const Tabs = ({ count, onOffsetChange, offset }) => {
		const groupSize = PAGE_SIZE;
		const numPages = Math.ceil(count / groupSize);
		const currentPage = Math.floor(offset / groupSize);

		if (numPages <= 1) return null;

		const pages: (number | 'ellipsis')[] = [0];
		const rangeStart = Math.max(1, currentPage - 1);
		const rangeEnd = Math.min(numPages - 2, currentPage + 1);
		if (rangeStart > 1) pages.push('ellipsis');
		for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
		if (rangeEnd < numPages - 2) pages.push('ellipsis');
		if (numPages > 1) pages.push(numPages - 1);

		return (
			<Flex justify="center" align="center" gap={1}>
				<Button size="sm" variant="outline" onClick={() => onOffsetChange(Math.max(0, offset - groupSize))} isDisabled={currentPage === 0}>‹</Button>
				{pages.map((page, idx) =>
					page === 'ellipsis' ? (
						<Text key={`e-${idx}`} px={2} color="gray.400">…</Text>
					) : (
						<Button key={page} size="sm" colorScheme={currentPage === page ? 'blue' : 'gray'} variant={currentPage === page ? 'solid' : 'outline'} onClick={() => onOffsetChange((page as number) * groupSize)}>
							{(page as number) + 1}
						</Button>
					)
				)}
				<Button size="sm" variant="outline" onClick={() => onOffsetChange(Math.min((numPages - 1) * groupSize, offset + groupSize))} isDisabled={currentPage === numPages - 1}>›</Button>
			</Flex>
		);
	};

	const columnHelper = createColumnHelper();
	const columns = [
		columnHelper.accessor('order_id', {
			cell: (info) => info.getValue(),
			header: 'ID',
		}),
		columnHelper.accessor('facility_name', {
			cell: (info) => info.getValue(),
			header: 'FACILITY',
		}),
		columnHelper.accessor('status', {
			cell: (info) => {
				const status = info.getValue() as string;
				const lowerCaseStatus = status.toLowerCase();
				let colorScheme = 'gray';

				if (lowerCaseStatus === 'pending') {
					// I assume this is what you want to change
					colorScheme = 'yellow';
				} else if (
					lowerCaseStatus === 'completed' ||
					lowerCaseStatus === 'shipped'
				) {
					colorScheme = 'green';
				} else if (lowerCaseStatus === 'canceled') {
					colorScheme = 'red';
				}

				return <Badge colorScheme={colorScheme}>{status}</Badge>;
			},
			header: 'STATUS',
		}),
		columnHelper.accessor('total_amount', {
			cell: (info) => {
				const val = info.getValue();
				return val != null ? `$${parseFloat(val).toFixed(2)}` : '—';
			},
			header: 'TOTAL',
		}),
		columnHelper.accessor('shipping_address', {
			cell: (info) => info.getValue(),
			header: 'SHIPPING ADDRESS',
		}),
		columnHelper.accessor('email', {
			cell: (info) => info.getValue(),
			header: 'USER Email',
		}),
		columnHelper.accessor('created_at', {
			cell: (info) => {
				const date = info.getValue();
				const ct = moment
					.tz(date, 'America/Chicago')
					.format('YYYY-MM-DD hh:mm A');
				return ct;
			},
			header: 'CREATED AT',
		}),
		columnHelper.display({
			id: 'action',
			cell: ({ row }) => (
				<HStack spacing={2}>
					<Tooltip label="View" aria-label="View tooltip">
						<Button size="xs" colorScheme="blue" onClick={() => viewOrder(row)}>
							<ViewIcon mr={1} />
							View
						</Button>
					</Tooltip>
				</HStack>
			),
			header: 'ACTION',
		}),
	];

	const table = useReactTable({
		columns,
		data: orders?.data || [],
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<>
			<Alert />
			<AdminNavbar />
			<Box pt={{ base: 16, md: 6 }} px={{ base: 4, md: 6 }} pb={6} ml={{ base: 0, md: "250px" }} bg="gray.50" minH="100vh">
				<Heading mb={6} fontSize="2xl" fontWeight="bold" color="gray.700">
					Order Management
				</Heading>
				<Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
					<SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 6 }} spacing={3} mb={3}>
						<Input
							placeholder="Order ID"
							size="sm"
							borderRadius="md"
							value={orderIdFilter}
							onChange={(e) => setOrderIdFilter(e.target.value)}
							isDisabled={adminorderloading}
						/>
						<Select
							placeholder="All Facilities"
							size="sm"
							borderRadius="md"
							value={facilityFilter}
							onChange={(e) => setFacilityFilter(e.target.value)}
							isDisabled={adminorderloading || facilitiesLoading}
						>
							{facilitiesList?.map((facility) => (
								<option key={facility.facilityId} value={facility.facilityId}>
									{facility.facilityName || facility.name}
								</option>
							))}
						</Select>
						<Input
							placeholder="User Email"
							size="sm"
							borderRadius="md"
							value={emailFilter}
							onChange={(e) => setEmailFilter(e.target.value)}
							isDisabled={adminorderloading}
						/>
						<Select
							placeholder="All Statuses"
							size="sm"
							borderRadius="md"
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							isDisabled={adminorderloading}
						>
							<option value="pending">Pending</option>
							<option value="shipped">Shipped</option>
							<option value="completed">Completed</option>
							<option value="canceled">Canceled</option>
						</Select>
						<Input
							type="date"
							size="sm"
							borderRadius="md"
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
							isDisabled={adminorderloading}
						/>
						<Input
							type="date"
							size="sm"
							borderRadius="md"
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
							isDisabled={adminorderloading}
						/>
					</SimpleGrid>
					<HStack justify="flex-end" spacing={2}>
						<Button
							size="sm"
							variant="ghost"
							colorScheme="gray"
							onClick={handleClearClick}
							isDisabled={adminorderloading}
						>
							Clear Filters
						</Button>
						<Button
							size="sm"
							colorScheme="blue"
							onClick={handleSearchClick}
							isLoading={isSearching}
							leftIcon={<SearchIcon />}
						>
							Search
						</Button>
					</HStack>
				</Box>

				<Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
					<Box overflowX="auto">
						<Table variant="simple" size="sm" minW="700px">
							<Thead bg="gray.100">
								{table.getHeaderGroups().map((headerGroup) => (
									<Tr key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<Th key={header.id} color="gray.600" fontSize="xs">
												{header.isPlaceholder
													? null
													: flexRender(
															header.column.columnDef.header,
															header.getContext()
													  )}
											</Th>
										))}
									</Tr>
								))}
							</Thead>
							{!adminorderloading && (
								<Tbody>
									{table.getRowModel().rows.map((row) => (
										<Tr
											key={row.id}
											_hover={{ bg: 'blue.100' }}
											transition="background 0.2s ease"
										>
											{row.getVisibleCells().map((cell) => (
												<Td key={cell.id} fontSize="sm" py={3}>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext()
													)}
												</Td>
											))}
										</Tr>
									))}
								</Tbody>
							)}
						</Table>
					</Box>
					{adminorderloading && (
						<Center p={10}>
							<Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
						</Center>
					)}
					{!adminorderloading && table.getRowModel().rows.length === 0 && (
						<Center p={10}>No orders match your criteria.</Center>
					)}
					{!adminorderloading && orderCount > 0 && (
						<Flex
							px={4}
							py={3}
							borderTop="1px solid"
							borderColor="gray.100"
							justify="space-between"
							align="center"
							flexWrap="wrap"
							gap={3}
						>
							<Text fontSize="sm" color="gray.500">
								Showing {Math.min(offset + 1, orderCount)}–{Math.min(offset + PAGE_SIZE, orderCount)} of {orderCount}
							</Text>
							<Tabs count={orderCount} onOffsetChange={setOffset} offset={offset} />
						</Flex>
					)}
				</Box>
			</Box>
		</>
	);
};

const mapStateToProps = (state) => ({
	orders: state.order.adminorder,
	adminorderloading: state.order.adminorderloading,
	facilitiesList: state.facilities.facList, // Map facilities list state
	facilitiesLoading: state.facilities.facListLoading, // Map loading state
});

const mapDispatchToProps = {
	getAllAdminOrders: (offset, orderId, facility, email, status, start, end) =>
		getAllAdminOrders(offset, orderId, facility, email, status, start, end),
	getAllFacilityList, // Add the new action to props
};

export default connect(mapStateToProps, mapDispatchToProps)(Order);
